import React, { useEffect } from 'react';

import { Controller, UseFormReturn } from 'react-hook-form';
import { sortAlphabeticalArray } from 'utilitys/helpers/general';

import { IAssigneeSingleOption } from 'hooks/useAssigneeOptionsList';

import { ICardAssignees, useQueryGetCardAssignees } from 'api/queries/getCardAssignees.query';

import classes from 'components/common/GlobalFiltersInPage/Filters.module.scss';
import { FiltersStatus } from 'components/common/GlobalFiltersInPage/GlobalFiltersInPage';
import { IAssignee } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.types';
import { FilterLabels } from 'components/common/GlobalFiltersInPage/globalFiltersInPage.utils';
import { signalSingleFilterIsReady } from 'components/common/GlobalFiltersInPage/hooks/useSubscribersFiltersReady';

import { Select } from 'components/DesignSystem/Select';
import { OptionsWithAssignee } from 'components/DesignSystem/Select/components/OptionsWithAssignee/OptionsWithAssignee';

interface IUpdatedByFilterProps {
    methods?: UseFormReturn<any, any>;
    name: string;
    filtersStatusRef?: React.MutableRefObject<FiltersStatus>;
    handleFilters?: () => void;
    value?: string;
    options?: ICardAssignees[];
    isSelected?: boolean;
    versionId?: number;
}

export const UpdatedByFilter = ({
    methods,
    name,
    filtersStatusRef,
    handleFilters,
    value,
    isSelected = true,
    versionId,
}: IUpdatedByFilterProps) => {
    const { data, isSuccess } = useQueryGetCardAssignees({
        metaDataId: versionId,
    });

    useEffect(() => {
        if (!filtersStatusRef?.current || filtersStatusRef?.current[name]?.isReady || !isSuccess) {
            return;
        }

        signalSingleFilterIsReady({ filterId: name, isReady: isSuccess });
    }, [value, name, filtersStatusRef, isSuccess]);

    const assigneeMapper = (i, isCurrenUser = false) => ({
        id: null,
        value: i.userName,
        label: `${i.lastName && i.lastName?.length > 0 ? `${i.firstName} ${i.lastName}` : i.firstName}${isCurrenUser ? ' (Current User)' : ''}`,
        assignee: { ...i, id: null },
    });

    const options = data
        ? [
              ...sortAlphabeticalArray(
                  data.map((item) => assigneeMapper(item)),
                  'label',
                  'asc',
              ),
          ]
        : [];

    const handleChange = (selectedOption: IAssigneeSingleOption | null) => {
        if (selectedOption) {
            methods?.setValue(name, selectedOption.assignee.userName);
        } else {
            methods?.setValue(name, null);
        }
        handleFilters && handleFilters();
    };

    const handleValue = (value) => {
        if (!value) {
            return null;
        }
        return options?.find((item) => item.assignee.userName === value) || null;
    };

    return isSelected ? (
        <div data-testid={'battlecards-filters-updated-by'} className={classes.selectWrapper}>
            <Controller
                render={({ field }) => (
                    <Select<IAssignee>
                        id={'battlecards-filters-updated-by'}
                        {...field}
                        onChange={handleChange}
                        options={options}
                        value={handleValue(value)}
                        components={{
                            Option: OptionsWithAssignee,
                        }}
                        label={FilterLabels.UPDATED_USER_CARD_BOARD}
                        isClearable
                    />
                )}
                name={name}
                control={methods?.control}
            />
        </div>
    ) : null;
};
